/**
 * The turn-flow engine core. Splitting resolution from application means the
 * loop-continuation site can read a continuation vote without owning a base
 * request: {@link resolveFlow} folds resolvers into transforms + continuation
 * purely and synchronously; {@link applyTurnRequest} later folds those
 * transforms over a concrete base request.
 */

import type { Continuation, FlowResolverEntry, ReadonlyTranscript, TurnRequest, TurnTransform } from './types.ts';

const DEFAULT_CONTINUATION: Continuation = 'continue';

/**
 * Fold a set of resolvers into an ordered transform list and a single
 * continuation vote.
 *
 * Resolvers run ascending by priority (a copy is sorted — the input is never
 * mutated). Each resolver's transforms are concatenated in that order;
 * continuation is reduced last-writer-wins, defaulting to `'continue'` when no
 * resolver expresses one. Pure and synchronous.
 */
export function resolveFlow({
  transcript,
  resolvers,
}: {
  transcript: ReadonlyTranscript;
  resolvers: FlowResolverEntry[];
}): { transforms: TurnTransform[]; continuation: Continuation } {
  const outputs = [...resolvers].sort((a, b) => a.priority - b.priority).map((entry) => entry.resolve({ transcript }));
  const transforms = outputs.flatMap((output) => output.transforms);
  const continuation = outputs.reduce<Continuation>((acc, output) => output.continuation ?? acc, DEFAULT_CONTINUATION);
  return { transforms, continuation };
}

/**
 * Apply an ordered transform list to a base request. Folds asynchronously so a
 * later transform always sees the cumulative result of every earlier one.
 */
export function applyTurnRequest(base: TurnRequest, transforms: TurnTransform[]): Promise<TurnRequest> {
  return transforms.reduce<Promise<TurnRequest>>((acc, transform) => acc.then(transform), Promise.resolve(base));
}
